// Which lines the charts draw.
//
// Never all of them off: an empty chart reads as a broken card rather than as a
// setting, so the last one showing refuses to turn off. `aria-pressed`, like
// every other toggle in the app.
//
// **Not a `Segmented`**, which is one choice out of a handful: these are four
// independent switches over one picture, and three of them can be on at once.

import { HoverCard, styled, Text } from "../kit";
import { SERIES_KEYS, type SeriesKey, type SeriesShown, someShown } from "../tunerView";
import { SERIES_COLOUR, SERIES_DASH, wash } from "./palette";

/** What each line is called, and the rule for reading it. */
export const SERIES_LABEL: Record<SeriesKey, { name: string; rule: string }> = {
  offers: {
    name: "offered",
    rule:
      "Every score an island put forward on this basis. It bounces, which is what makes it " +
      "useful — the running best cannot fall, so on its own it cannot show a search that has " +
      "stopped finding anything."
  },
  best: {
    name: "best",
    rule:
      "The running best within this basis. Monotonic by construction, so a flat stretch is a " +
      "search that has not broken its record — not necessarily one that has stopped."
  },
  foe: {
    name: "loss line",
    rule:
      "What the armies opposite scored on the same games, under this same goal. A control and " +
      "never a target: both lines rising together is a batch that got easier, not a candidate " +
      "that got better. Nothing selects on it."
  },
  pending: {
    name: "live",
    rule:
      "Where the running islands are now — the newest generation each has finished, which is the " +
      "number that becomes the next point if it beats the best. Marked ▲ when it already does. " +
      "Not a candidate mid-measurement: a candidate has no score at all until its whole batch " +
      "has been played and scored."
  }
};

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.kit.space.xs,
  marginBottom: theme.kit.space.md
}));

/**
 * One switch, wearing its own line's colour when it is on.
 *
 * A `Btn` would be right if the four were interchangeable; they are not — the
 * whole job of the control is to say *which line* it governs, so the swatch and
 * the lit edge are the line's own colour rather than the accent.
 */
const Switch = styled("button", {
  shouldForwardProp: (prop: string) => prop !== "on" && prop !== "hue"
})<{ on?: boolean; hue: string }>(({ theme, on, hue }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.kit.space.xs,
  padding: "5px 11px",
  border: `2px solid ${on ? hue : theme.kit.color.line}`,
  borderRadius: theme.kit.radius.sm,
  background: on ? wash(hue, 14) : "transparent",
  color: on ? theme.kit.color.ink : theme.kit.color.inkDim,
  fontFamily: theme.kit.font.body,
  fontWeight: 700,
  fontSize: 12,
  cursor: "pointer",
  "&:focus-visible": { outline: `2px solid ${theme.kit.color.brand}`, outlineOffset: 2 },
  '&[aria-disabled="true"]': { cursor: "default", opacity: 0.75 }
}));

/** The line itself, drawn — dash and all, so the pattern is the fifth axis. */
const Swatch = styled("svg")({ display: "block", flexShrink: 0 });

export function SeriesToggles({
  shown,
  onChange
}: {
  shown: SeriesShown;
  onChange(next: SeriesShown): void;
}): React.ReactElement {
  return (
    <Row>
      {SERIES_KEYS.map((key) => {
        const on = shown[key];
        const label = SERIES_LABEL[key];
        const hue = SERIES_COLOUR[key];
        // The last one standing stays on, or every card goes blank at once.
        const last = on && !someShown({ ...shown, [key]: false });
        return (
          <HoverCard
            key={key}
            placement="bottom"
            title={label.name}
            card={
              last ? (
                // Two lines, not one string with newlines in it: a card's body
                // is a block, so `\n` in it is a space.
                <>
                  <Text block tone="dim" size={12}>
                    {label.rule}
                  </Text>
                  <Text block tone="dim" size={12}>
                    The last line showing — something has to be drawn.
                  </Text>
                </>
              ) : (
                label.rule
              )
            }
            maxWidth={340}
          >
            <Switch
              type="button"
              hue={hue}
              on={on}
              aria-pressed={on}
              aria-disabled={last || undefined}
              data-testid={`series-${key}`}
              onClick={last ? undefined : () => onChange({ ...shown, [key]: !on })}
            >
              <Swatch width={14} height={4} viewBox="0 0 14 4" aria-hidden>
                <line
                  x1={0}
                  x2={14}
                  y1={2}
                  y2={2}
                  stroke={on ? hue : "currentColor"}
                  strokeWidth={2}
                  strokeDasharray={SERIES_DASH[key] || undefined}
                  strokeOpacity={on ? 1 : 0.5}
                />
              </Swatch>
              {label.name}
            </Switch>
          </HoverCard>
        );
      })}
    </Row>
  );
}
